import { computed, ref, type ComputedRef, type Ref } from 'vue';
import {
  cellKey,
  cellsInRect,
  inRect,
  parseCellKey,
  rectBetween,
  type Cell,
  type Rect,
} from '@/utils/gridRange';

/**
 * Reactive Excel-like selection model for a 2-D grid: a rectangular range
 * (anchor → focus) plus a non-contiguous multi-cell set that, once built,
 * supersedes the rectangle. It owns *only* selection state — no editing, no
 * persistence, no DOM — so a host can drive it from template/document events
 * and compose it with fill/clipboard/keyboard behaviours.
 *
 * The mutators map 1:1 to interaction intents (a plain click vs. shift-click
 * vs. ⌘/Ctrl-click vs. a drag) so the host's event handlers stay declarative.
 */
export interface GridSelectionOptions {
  /** Current row count (a getter so it tracks a reactive rows array). */
  rowCount: () => number;
  /** Current column count. */
  colCount: () => number;
}

export interface GridSelection {
  /** Where the current range began. */
  anchor: Ref<Cell | null>;
  /** The range's moving/active end; also the "active cell" for keyboard nav. */
  focus: Ref<Cell | null>;
  /** ⌘/Ctrl-clicked cell keys; non-empty supersedes the rectangle. */
  multiCells: Ref<Set<string>>;
  /** True while a drag-select is in progress (set by {@link startAt}). */
  isSelecting: Ref<boolean>;
  /** The active rectangle (inclusive), or null when nothing is selected. */
  selRect: ComputedRef<Rect | null>;
  /** Whether a cell is currently highlighted (multi set or rectangle). */
  isSelected(ri: number, ci: number): boolean;
  /** Whether the drag-fill handle belongs on this cell (rectangle's SE corner). */
  isFillHandle(ri: number, ci: number): boolean;
  /** Every selected [ri, ci] — the multi set if present, else the rectangle. */
  selectedCells(): Array<[number, number]>;
  /** Move the active cell, optionally extending the range (Shift+arrows). */
  moveSelection(dr: number, dc: number, extend?: boolean): void;
  /** Reset the pending-click suppressor at the start of a fresh interaction. */
  beginInteraction(): void;
  /** True (and clears) if the click that just fired should be swallowed. */
  consumeClick(): boolean;
  /** Plain click / drag start: collapse to this cell and arm drag-select. */
  startAt(ri: number, ci: number): void;
  /** ⌘/Ctrl-click: toggle this cell in the non-contiguous set. */
  toggleMulti(ri: number, ci: number): void;
  /** Shift-click: extend the rectangle to this cell (no drag). */
  extendTo(ri: number, ci: number): void;
  /** Drag over a cell; returns true when it moved off the anchor (a real drag). */
  dragTo(ri: number, ci: number): boolean;
  /** End an in-progress drag-select. */
  endDrag(): void;
  /** Set the rectangle explicitly (paste block / post-fill selection). */
  setSelection(a: Cell, b: Cell): void;
  /** Drop the non-contiguous set back to the rectangle. */
  collapseMulti(): void;
  /** Escape: collapse the rectangle and any multi-set to the active cell. */
  collapseToFocus(): void;
  /** Clear everything (e.g. when the underlying data set changes). */
  reset(): void;
}

export function useGridSelection(opts: GridSelectionOptions): GridSelection {
  const anchor = ref<Cell | null>(null);
  const focus = ref<Cell | null>(null);
  const multiCells = ref<Set<string>>(new Set());
  const isSelecting = ref(false);
  // A drag ends with a stray `click` on the common ancestor; this latch lets the
  // host swallow that click so it doesn't open an editor. Non-reactive: only the
  // host's click handler reads it, via consumeClick().
  let suppressClick = false;

  const selRect = computed<Rect | null>(() =>
    anchor.value && focus.value ? rectBetween(anchor.value, focus.value) : null,
  );

  function isSelected(ri: number, ci: number): boolean {
    if (multiCells.value.size) return multiCells.value.has(cellKey(ri, ci));
    return inRect(selRect.value, ri, ci);
  }

  function isFillHandle(ri: number, ci: number): boolean {
    if (multiCells.value.size) return false; // no single corner for a scattered set
    const s = selRect.value;
    return !!s && ri === s.r2 && ci === s.c2;
  }

  function selectedCells(): Array<[number, number]> {
    if (multiCells.value.size) {
      return [...multiCells.value].map((k) => {
        const { ri, ci } = parseCellKey(k);
        return [ri, ci] as [number, number];
      });
    }
    return selRect.value ? cellsInRect(selRect.value) : [];
  }

  function moveSelection(dr: number, dc: number, extend = false): void {
    const base = focus.value ?? anchor.value ?? { ri: 0, ci: 0 };
    const ri = Math.min(opts.rowCount() - 1, Math.max(0, base.ri + dr));
    const ci = Math.min(opts.colCount() - 1, Math.max(0, base.ci + dc));
    focus.value = { ri, ci };
    if (!extend) anchor.value = { ri, ci };
  }

  function beginInteraction(): void {
    suppressClick = false;
  }

  function consumeClick(): boolean {
    if (suppressClick) {
      suppressClick = false;
      return true;
    }
    return false;
  }

  function startAt(ri: number, ci: number): void {
    // A plain click inside the multi set keeps it (so you can edit a member and
    // still ⌘/Ctrl+Enter to fill them all); clicking outside collapses.
    if (!multiCells.value.has(cellKey(ri, ci))) multiCells.value = new Set();
    anchor.value = { ri, ci };
    focus.value = { ri, ci };
    isSelecting.value = true;
  }

  function toggleMulti(ri: number, ci: number): void {
    const key = cellKey(ri, ci);
    const next = new Set(multiCells.value);
    // Seed from the current rectangle so an existing selection is preserved.
    if (next.size === 0 && selRect.value) {
      for (const [r, c] of cellsInRect(selRect.value)) next.add(cellKey(r, c));
    }
    if (next.has(key)) next.delete(key);
    else next.add(key);
    multiCells.value = next;
    anchor.value = { ri, ci };
    focus.value = { ri, ci };
    suppressClick = true;
  }

  function extendTo(ri: number, ci: number): void {
    multiCells.value = new Set();
    focus.value = { ri, ci };
    suppressClick = true;
  }

  function dragTo(ri: number, ci: number): boolean {
    const a = anchor.value ?? { ri, ci };
    const moved = ri !== a.ri || ci !== a.ci;
    if (moved) {
      suppressClick = true;
      multiCells.value = new Set();
    }
    focus.value = { ri, ci };
    return moved;
  }

  function endDrag(): void {
    isSelecting.value = false;
  }

  function setSelection(a: Cell, b: Cell): void {
    anchor.value = { ...a };
    focus.value = { ...b };
  }

  function collapseMulti(): void {
    multiCells.value = new Set();
  }

  function collapseToFocus(): void {
    if (focus.value) anchor.value = { ...focus.value };
    multiCells.value = new Set();
  }

  function reset(): void {
    anchor.value = null;
    focus.value = null;
    multiCells.value = new Set();
    isSelecting.value = false;
  }

  return {
    anchor,
    focus,
    multiCells,
    isSelecting,
    selRect,
    isSelected,
    isFillHandle,
    selectedCells,
    moveSelection,
    beginInteraction,
    consumeClick,
    startAt,
    toggleMulti,
    extendTo,
    dragTo,
    endDrag,
    setSelection,
    collapseMulti,
    collapseToFocus,
    reset,
  };
}
